import React from 'react';
import { useTranslation } from 'react-i18next';
import { PapiDeviation } from '@wix/da-papi-types';
import { BiData, AdoptAdoptablesClickBiEvent } from '@wix/da-bi/pkg/events';
import ExclusiveButton from '@wix/da-ds/pkg/Buttons/ExclusiveButton';
import GiftButton from '../../lego/GiftButton';
import Flex from '@wix/da-ds/pkg/Flex';
import FlexItem from '@wix/da-ds/pkg/Flex/Item';
import { UseAdoptableStateReturnType } from '../../../AdoptableInfo/useAdoptableState';
import { formatDollars } from '../../../../../../utils/formatting';
import { getDeviationSaleDetails } from '../../../../../../utils/deviations';
import { useBuyNowAction, useMakeOfferAction, useManageOfferAction } from '../../hooks';
import PriceWithDiscount from '../../../../../../PriceWithDiscount';
import DiscountsPopupTrigger from '../../../../../../DiscountsPopupTrigger';

interface Props {
  deviation: PapiDeviation;
  adoptableState: UseAdoptableStateReturnType;
}

const BuyerButtons: React.FC<Props> = ({ deviation, adoptableState }) => {
  const { t } = useTranslation();
  const { canBuyNow, isGiftable, canMakeNewOffer, canManageExistingOffer, fixedPrice } =
    adoptableState;

  const sectionname = 'side-rail';
  const [onMakeOffer, makeOfferBiData] = useMakeOfferAction(deviation, {
    sectionname,
  });
  const [onManageOffer, manageOfferBiData] = useManageOfferAction(deviation, {
    sectionname,
  });
  const [onBuyNow, buyNowBiData] = useBuyNowAction(deviation, {
    sectionname,
    purchase_price: fixedPrice,
  });
  const { eligibleDiscount, allDiscounts, hasDiscounts } = getDeviationSaleDetails(deviation);
  const showDiscount = hasDiscounts;

  if (isGiftable) {
    return (
      <React.Fragment>
        {canBuyNow && showDiscount && (
          <>
            <ExclusiveButton
              key="buy-now"
              priority="primary"
              onClick={onBuyNow}
              biData={BiData<AdoptAdoptablesClickBiEvent>(buyNowBiData)}
            >
              <PriceWithDiscount
                dollarAmount={fixedPrice}
                eligibleDiscount={eligibleDiscount}
                prefix={t('sidebar.exclusives.buttons.buyer.buyFor')}
                size="medium"
                variant="exclusive"
                opaque
              />
            </ExclusiveButton>
            <DiscountsPopupTrigger
              allDiscounts={allDiscounts}
              eligibleDiscount={eligibleDiscount}
            />
          </>
        )}
        {canBuyNow && !showDiscount && (
          <ExclusiveButton
            key="buy-now"
            priority="primary"
            onClick={onBuyNow}
            biData={BiData<AdoptAdoptablesClickBiEvent>(buyNowBiData)}
          >
            {t('sidebar.exclusives.buttons.buyer.buyNow', {
              price: formatDollars(fixedPrice, {
                useGrouping: true,
              }),
            })}
          </ExclusiveButton>
        )}
        {canBuyNow && canMakeNewOffer && (
          <Flex fullWidth gap={8}>
            <FlexItem basis="50%">
              <ExclusiveButton
                key="make-offer"
                priority="secondary"
                variant="compact"
                onClick={onMakeOffer}
                biData={makeOfferBiData}
              >
                {t('sidebar.exclusives.buttons.buyer.makeOffer')}
              </ExclusiveButton>
            </FlexItem>
            <FlexItem basis="50%">
              <GiftButton deviation={deviation} adoptableState={adoptableState} />
            </FlexItem>
          </Flex>
        )}
        {canBuyNow && !canMakeNewOffer && (
          <Flex fullWidth>
            <FlexItem basis="100%">
              <GiftButton deviation={deviation} adoptableState={adoptableState} />
            </FlexItem>
          </Flex>
        )}
        {canManageExistingOffer && (
          <ExclusiveButton
            key="manage-offer"
            priority="secondary"
            onClick={onManageOffer}
            biData={manageOfferBiData}
          >
            {t('sidebar.exclusives.buttons.buyer.manageOffer')}
          </ExclusiveButton>
        )}
      </React.Fragment>
    );
  }

  return (
    <React.Fragment>
      {canBuyNow && showDiscount && (
        <>
          <ExclusiveButton
            key="buy-now"
            priority={canMakeNewOffer || canManageExistingOffer ? 'secondary' : 'primary'}
            onClick={onBuyNow}
            biData={BiData<AdoptAdoptablesClickBiEvent>(buyNowBiData)}
          >
            <PriceWithDiscount
              dollarAmount={fixedPrice}
              eligibleDiscount={eligibleDiscount}
              prefix={t('sidebar.exclusives.buttons.buyer.buyFor')}
              size="medium"
              variant="exclusive"
              opaque
            />
          </ExclusiveButton>

          <DiscountsPopupTrigger allDiscounts={allDiscounts} eligibleDiscount={eligibleDiscount} />
        </>
      )}
      {canBuyNow && !showDiscount && (
        <ExclusiveButton
          key="buy-now"
          priority={canMakeNewOffer || canManageExistingOffer ? 'secondary' : 'primary'}
          onClick={onBuyNow}
          biData={BiData<AdoptAdoptablesClickBiEvent>(buyNowBiData)}
        >
          {t('sidebar.exclusives.buttons.buyer.buyNow', {
            price: formatDollars(fixedPrice, {
              useGrouping: true,
            }),
          })}
        </ExclusiveButton>
      )}
      {canMakeNewOffer && (
        <ExclusiveButton
          key="make-offer"
          priority="primary"
          onClick={onMakeOffer}
          biData={makeOfferBiData}
        >
          {t('sidebar.exclusives.buttons.buyer.makeOffer')}
        </ExclusiveButton>
      )}
      {canManageExistingOffer && (
        <ExclusiveButton
          key="manage-offer"
          priority="primary"
          onClick={onManageOffer}
          biData={manageOfferBiData}
        >
          {t('sidebar.exclusives.buttons.buyer.manageOffer')}
        </ExclusiveButton>
      )}
    </React.Fragment>
  );
};
BuyerButtons.displayName = 'BuyerButtons';

export default BuyerButtons;
